import api from "./api";

export interface RecommendedSet {
  set: number;
  reps?: number;
  kg?: number;
  time?: number;
  meters?: number;
  minutes?: number;
  kilometers?: number;
}

export interface TrainerWorkoutExercisePayload {
  exerciseId: number;
  position?: number;
  restTimeSec?: number;
  recommendedSets?: RecommendedSet[];
  notes?: string;
}

export interface TrainerWorkoutPayload {
  name: string;
  description?: string;
  dayOfWeek?: string;
  trackType?: "strength" | "strength_conditioning" | "home";
  blockType?: "warmup" | "main" | "finisher" | "cooldown";
  isRestDay?: boolean;
  restDayMessage?: string;
  duration?: number;
  position?: number;
  exercises?: TrainerWorkoutExercisePayload[];
}

export interface TrainerProgramPayload {
  title: string;
  description?: string;
  bannerUrl?: string;
  trainerId?: number;
  isActive?: boolean;
  workouts?: TrainerWorkoutPayload[];
}

export interface TrainerProgram {
  id: number;
  title: string;
  description?: string;
  bannerUrl?: string;
  isActive: boolean;
  trainer?: { id: number; name: string; photoUrl?: string };
  workouts: {
    id: number;
    name: string;
    description?: string;
    dayOfWeek?: string;
    trackType?: string;
    blockType?: "warmup" | "main" | "finisher" | "cooldown";
    isRestDay: boolean;
    restDayMessage?: string;
    duration?: number;
    position: number;
    exercises: {
      id: number;
      position: number;
      restTimeSec?: number;
      notes?: string;
      section: "warmup" | "main" | "cooldown";
      recommendedSets: RecommendedSet[];
      exercise: { id: number; name: string; exerciseType?: string; videoUrls?: { url: string; thumbnail: string }[]; swapOptions?: { id: number; name: string }[] };
    }[];
  }[];
  createdAt: string;
}

export interface TrainerProgramsResponse {
  programs: TrainerProgram[];
  totalPages: number;
  total: number;
}

const BASE = "/trainer-programs";

export const getTrainerPrograms = async (
  page = 1,
  limit = 10,
  trainerId?: number | ""
): Promise<TrainerProgramsResponse> => {
  const params: Record<string, unknown> = { page, limit };
  if (trainerId) params.trainerId = trainerId;
  const response = await api.get<TrainerProgramsResponse>(BASE, { params });
  return response.data;
};

export const getTrainerProgram = async (id: number): Promise<TrainerProgram> => {
  const response = await api.get<TrainerProgram>(`${BASE}/${id}`);
  return response.data;
};

export const createTrainerProgram = async (
  payload: TrainerProgramPayload
): Promise<TrainerProgram> => {
  const response = await api.post<TrainerProgram>(BASE, payload);
  return response.data;
};

export const updateTrainerProgram = async (
  id: number,
  payload: TrainerProgramPayload
): Promise<TrainerProgram> => {
  const response = await api.put<TrainerProgram>(`${BASE}/${id}`, payload);
  return response.data;
};

export const deleteTrainerProgram = async (id: number): Promise<void> => {
  await api.delete(`${BASE}/${id}`);
};

export const getTrainerProgramParticipantsCount = async (
  id: number
): Promise<number> => {
  const response = await api.get<{ count: number }>(`${BASE}/${id}/participants-count`);
  return response.data.count;
};

export const setTrainerProgramStatus = async (
  id: number,
  isActive: boolean
): Promise<TrainerProgram> => {
  const response = await api.patch<TrainerProgram>(`${BASE}/${id}/status`, {
    isActive,
  });
  return response.data;
};
